import { NavLink } from 'react-router-dom';
import { Home, Users, Wallet, Bell, User, Receipt, PiggyBank, CreditCard } from 'lucide-react';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { TierLevel } from '../../../domain/enums/TierLevel';
import './BottomNav.css';

export interface NavItem {
  to: string;
  labelKey: string;
  icon: typeof Home;
}

const TIER_RANK: Record<string, number> = { L1: 1, L2: 2, L3: 3, L4: 4, L5: 5, L6: 6, ADMIN: 99 };

/**
 * W02 — 5-item sticky bottom nav, role-adaptive (hidden above 768px).
 * Payment/commission items lock for DISABLED_DEFAULT via delinquencyLock passed by shell.
 */
export function MobileBottomNav({ tier, delinquencyLocked }: { tier: TierLevel; delinquencyLocked: boolean }) {
  const { t } = useTranslation();
  const rank = TIER_RANK[tier] ?? 0;

  // BD-31: agents own no subscription, subscribers earn no commission — each gets its own five tabs.
  const visible: NavItem[] =
    rank >= TIER_RANK[TierLevel.L2]
      ? [
          { to: '/', labelKey: 'nav.dashboard', icon: Home },
          { to: '/commissions', labelKey: 'nav.commissions', icon: Wallet },
          { to: '/team', labelKey: 'nav.team', icon: Users },
          { to: '/withdrawals', labelKey: 'nav.withdrawals', icon: PiggyBank },
          { to: '/profile', labelKey: 'nav.profile', icon: User },
        ]
      : [
          { to: '/', labelKey: 'nav.dashboard', icon: Home },
          { to: '/installments', labelKey: 'nav.installments', icon: Receipt },
          { to: '/payments', labelKey: 'nav.payments', icon: CreditCard },
          { to: '/notices', labelKey: 'nav.notices', icon: Bell },
          { to: '/profile', labelKey: 'nav.profile', icon: User },
        ];

  return (
    <nav className="bottom-nav show-mobile" aria-label="Primary">
      {visible.map(({ to, labelKey, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={to === '/'}
          className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}${delinquencyLocked ? ' locked' : ''}`}
          data-testid={`nav-${to === '/' ? 'home' : to.slice(1)}`}
        >
          <Icon size={20} aria-hidden />
          <span>{t(labelKey)}</span>
        </NavLink>
      ))}
    </nav>
  );
}
